import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  InputAdornment,
  Alert,
  Tooltip,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SyncIcon from '@mui/icons-material/Sync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import PrintIcon from '@mui/icons-material/Print';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import UndoIcon from '@mui/icons-material/Undo';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SavedCardsList from '../SavedCardsList';
import CreditCardAffidavitButton from '../CreditCardAffidavitButton';
import RequestPaymentButton from '../RequestPaymentButton';
import { StateManager } from '../../utilities/stateManager';
import constants from '../../utilities/constants';
import firebase from '../../utilities/firebase';
import moment from 'moment';
import ExpenseModal from '../ExpenseModal';
import DepositModal from '../DepositModal';
import { computeEstimateTotals } from '../../utilities/estimateTotals';
import ClawbackDialog from './ClawbackDialog';

const isValidNumber = (value) => {
  return !(value === null || value === undefined || value === '');
};

export default function AccountingTab({ order, stockNumber, setOrder }) {
  const { car = {}, customer = {} } = order;
  const [revenue, setRevenue] = React.useState(order.revenue || 0);
  const [revenueInput, setRevenueInput] = React.useState(
    order.revenue ? order.revenue.toString() : ""
  );
  const [salesTax, setSalesTax] = React.useState(order.salesTax || 0);
  const [salesTaxInput, setSalesTaxInput] = React.useState(
    order.salesTax ? order.salesTax.toString() : ""
  );
  const [purchasesExpanded, setPurchasesExpanded] = React.useState(false);
  const [depositsExpanded, setDepositsExpanded] = React.useState(false);
  const [selectedExpense, setSelectedExpense] = React.useState(null);
  const [expenseModalOpen, setExpenseModalOpen] = React.useState(false);
  const [depositModalOpen, setDepositModalOpen] = React.useState(false);
  const [selectedDeposit, setSelectedDeposit] = React.useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = React.useState(null);
  const [clawbackOpen, setClawbackOpen] = React.useState(false);

  const openDepositReceipt = (depositId) => {
    window.open(`/receipt/deposit/${depositId}/print`, '_blank');
  };
  const openStatement = () => {
    setPrintMenuAnchor(null);
    window.open(`/receipt/order/${stockNumber}/statement/print`, '_blank');
  };
  const openInvoice = () => {
    setPrintMenuAnchor(null);
    window.open(`/receipt/order/${stockNumber}/invoice/print`, '_blank');
  };
  const lastSyncedRevenueRef = React.useRef(order.revenue);
  const lastSyncedSalesTaxRef = React.useRef(order.salesTax);

  // Live timeLogs for this SO. Used to compute Clocked Labor independently of the
  // services list. The auto "Labor — {Mechanic}" service lines are filtered out of
  // service labor below so they don't double-count with these.
  const [timeLogs, setTimeLogs] = React.useState([]);
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    if (!stockNumber) return;
    const unsub = firebase.firestore()
      .collection('timeLogs')
      .where('serviceOrderId', '==', stockNumber)
      .onSnapshot(
        (snap) => setTimeLogs(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        (err) => console.error('AccountingTab timeLogs listener error:', err),
      );
    return () => unsub();
  }, [stockNumber]);
  React.useEffect(() => {
    if (!timeLogs.some(l => l.status === 'active')) return;
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, [timeLogs]);

  // Split-allocation model: order.customerPays = { type: 'amount', value: N } and
  // order.internalAmount = M. Customer and Internal are independent dollar inputs;
  // Revenue = Customer + Internal. When customerPays is absent, fall back to the
  // legacy revenue-only UI.
  const useSplitModel = !!(order.customerPays && order.customerPays.type);

  // Initial customer amount: coerce legacy percent values to a $ amount once.
  const initialCustomerAmount = (() => {
    const cp = order.customerPays;
    if (!cp || cp.value == null) return 0;
    if (cp.type === 'percent') {
      const legacyTotalCost = (order.services || []).reduce((a, c) => a + (c.cost || 0), 0)
        + (order.expenses || [])
            .filter(e => !(e.account === 'trade' && e.id === stockNumber))
            .reduce((a, c) => a + (c.amount || 0), 0);
      return Math.round(legacyTotalCost * (Number(cp.value) || 0)) / 100;
    }
    return Number(cp.value) || 0;
  })();

  // For SOs created before internalAmount existed as an explicit field, infer it
  // from totalCost - customerAmount so the input shows the value previously baked
  // into the auto-trade purchase doc.
  const initialInternalAmount = (() => {
    if (order.internalAmount != null) return Number(order.internalAmount);
    if (!useSplitModel) return 0;
    const legacyLaborCost = (order.services || []).reduce((a, c) => a + (c.cost || 0), 0);
    const legacyPurchasesTotal = (order.expenses || [])
      .filter(e => !(e.account === 'trade' && e.id === stockNumber))
      .reduce((a, c) => a + (c.amount || 0), 0);
    return Math.max(0, Math.round((legacyLaborCost + legacyPurchasesTotal - initialCustomerAmount) * 100) / 100);
  })();

  const [customerInput, setCustomerInput] = React.useState(
    initialCustomerAmount ? String(initialCustomerAmount) : ''
  );
  const [internalInput, setInternalInput] = React.useState(
    initialInternalAmount ? String(initialInternalAmount) : ''
  );
  const lastSyncedCustomerRef = React.useRef(initialCustomerAmount);
  const lastSyncedInternalRef = React.useRef(order.internalAmount);

  React.useEffect(() => {
    const cp = order.customerPays;
    let val = 0;
    if (cp && cp.value != null) {
      val = cp.type === 'percent'
        ? Math.round(((order.services || []).reduce((a, c) => a + (c.cost || 0), 0)
            + (order.expenses || []).filter(e => !(e.account === 'trade' && e.id === stockNumber)).reduce((a, c) => a + (c.amount || 0), 0))
            * (Number(cp.value) || 0)) / 100
        : Number(cp.value) || 0;
    }
    if (val !== lastSyncedCustomerRef.current) {
      lastSyncedCustomerRef.current = val;
      setCustomerInput(val ? String(val) : '');
    }
  }, [order.customerPays, order.services, order.expenses, stockNumber]);

  React.useEffect(() => {
    const val = order.internalAmount != null ? Number(order.internalAmount) : null;
    if (val !== lastSyncedInternalRef.current) {
      lastSyncedInternalRef.current = val;
      setInternalInput(val != null ? String(val) : '');
    }
  }, [order.internalAmount]);

  // Update revenue input when order.revenue changes externally
  React.useEffect(() => {
    const orderRevenue = order.revenue || 0;
    // Only sync if order.revenue changed from an external source (not from our own updates)
    if (orderRevenue !== lastSyncedRevenueRef.current) {
      lastSyncedRevenueRef.current = orderRevenue;
      setRevenue(orderRevenue);
      setRevenueInput(orderRevenue ? orderRevenue.toString() : "");
    }
  }, [order.revenue]);

  // Update sales tax input when order.salesTax changes externally
  React.useEffect(() => {
    const orderSalesTax = order.salesTax || 0;
    // Only sync if order.salesTax changed from an external source (not from our own updates)
    if (orderSalesTax !== lastSyncedSalesTaxRef.current) {
      lastSyncedSalesTaxRef.current = orderSalesTax;
      setSalesTax(orderSalesTax);
      setSalesTaxInput(orderSalesTax ? orderSalesTax.toString() : "");
    }
  }, [order.salesTax]);

  const depositsAmount = (order.deposits || []).reduce((a, c) => a + (c.amount || 0), 0);

  // Labor splits into two independent sources:
  //   - Service Labor: flat-rate services.cost, deconflicted against clocked labor
  //     via constants.countedServiceLaborCost — this excludes legacy auto "Labor —
  //     {Mechanic}" lines (source: 'clock') AND, for work completed on/after the
  //     clock-time cutoff, any line completed by a clock-in mechanic (their cost
  //     comes from timeLogs). Lines completed before the cutoff still count.
  //   - Clocked Labor: live sum of timeLog durations × rate (active sessions counted up to now).
  const serviceLabor = (order.services || [])
    .filter(s => !s.declined)
    .reduce((a, c) => a + constants.countedServiceLaborCost(c), 0);
  const clockedLabor = timeLogs.reduce((acc, log) => {
    const startMs = log.startTime && log.startTime.toMillis ? log.startTime.toMillis() : null;
    const endMs = log.endTime && log.endTime.toMillis
      ? log.endTime.toMillis()
      : (log.status === 'active' ? now : null);
    if (!startMs || !endMs || endMs <= startMs) return acc;
    return acc + ((endMs - startMs) / 3600000) * (Number(log.rate) || 0);
  }, 0);
  const laborCost = Math.round((serviceLabor + clockedLabor) * 100) / 100;
  const showLaborBreakdown = serviceLabor > 0 && clockedLabor > 0;

  // Calculate purchases (sum of expenses).
  // In split-allocation mode, exclude the auto-generated trade expense for this SO
  // (purchases/{soId} with account: 'trade') so it doesn't double-count when computing
  // internal cost.
  const purchasesTotal = (order.expenses || [])
    .filter(e => !(useSplitModel && e.account === 'trade' && e.id === stockNumber))
    .reduce((a, c) => a + (c.amount || 0), 0);

  const totalCost = laborCost + purchasesTotal;
  const parseDollar = (s) => parseFloat((s || '').replace(/,/g, '').replace('$', '').trim()) || 0;
  const customerAmount = parseDollar(customerInput);
  const internalAmount = parseDollar(internalInput);
  const splitRevenue = Math.round((customerAmount + internalAmount) * 100) / 100;
  const splitProfit = Math.round((splitRevenue - totalCost) * 100) / 100;

  // Calculate profit (legacy view: revenue - costs)
  const profit = revenue - laborCost - purchasesTotal;

  // Calculate total (customer billed + sales tax)
  const total = (useSplitModel ? customerAmount : revenue) + salesTax;

  const paymentDescription = `${car.year || ""} ${car.make || ""} ${car.model || ""}`;

  const handleRevenueChange = (e) => {
    const value = e.target.value;
    // Allow free typing - just store the raw input
    setRevenueInput(value);
  };

  // Persist a revenue value (shared by the manual blur and the sync-from-estimate button).
  const commitRevenue = (numericValue) => {
    setRevenue(numericValue);
    setRevenueInput(numericValue ? numericValue.toString() : "");
    lastSyncedRevenueRef.current = numericValue; // Update ref to prevent re-syncing

    // Sync bill to stock number if skyway is customer
    if(customer && customer.id === "9c0d88f5-84f9-454d-833d-a8ced9adad49"){
      const expense = {
        date: moment().format('MM/DD/YYYY'),
        isPayable: true,
        stock: car.stock,
        account: "trade",
        amount: numericValue,
        memo: stockNumber,
        vendor: "Skyway Classics"
      }
      firebase.firestore().collection("purchases").doc(stockNumber).set(expense, {merge: true});
    }

    // Save to database
    firebase.firestore().doc('orders/'+stockNumber).set({revenue: numericValue}, {merge: true});
    setOrder(prev => ({...prev, revenue: numericValue}));
  };

  const handleRevenueBlur = () => {
    // When user finishes typing, parse and save the value
    const cleanedValue = revenueInput.replace(/,/g, "").replace("$", "").trim();
    const numericValue = cleanedValue === "" ? 0 : parseFloat(cleanedValue) || 0;
    commitRevenue(numericValue);
  };

  const handleSalesTaxChange = (e) => {
    const value = e.target.value;
    // Allow free typing - just store the raw input
    setSalesTaxInput(value);
  };

  const handleSalesTaxBlur = () => {
    // When user finishes typing, parse and save the value
    const cleanedValue = salesTaxInput.replace(/,/g, "").replace("$", "").trim();
    const numericValue = cleanedValue === "" ? 0 : parseFloat(cleanedValue) || 0;
    
    setSalesTax(numericValue);
    setSalesTaxInput(numericValue ? numericValue.toString() : "");
    lastSyncedSalesTaxRef.current = numericValue; // Update ref to prevent re-syncing

    // Save to database
    firebase.firestore().doc('orders/'+stockNumber).set({salesTax: numericValue}, {merge: true});
    setOrder(prev => ({...prev, salesTax: numericValue}));
  };

  // Save the customer + internal allocation. Both are independent dollar amounts;
  // their sum is the revenue. Writes:
  //   - order.customerPays = { type: 'amount', value: customer$ }
  //   - order.internalAmount = internal$
  //   - order.revenue = customer$ + internal$
  //   - purchases/{soId} amount = internal$ (skipped when car.status === 'service')
  const saveAllocation = (nextCustomerInput, nextInternalInput) => {
    const customerVal = parseDollar(nextCustomerInput);
    const internalVal = parseDollar(nextInternalInput);
    const newRevenue = Math.round((customerVal + internalVal) * 100) / 100;

    const customerPaysUpdate = { type: 'amount', value: customerVal };
    lastSyncedCustomerRef.current = customerVal;
    lastSyncedInternalRef.current = internalVal;
    lastSyncedRevenueRef.current = newRevenue;

    setRevenue(newRevenue);
    setRevenueInput(newRevenue ? newRevenue.toString() : '');
    setCustomerInput(customerVal ? String(customerVal) : '');
    setInternalInput(internalVal ? String(internalVal) : '');

    firebase.firestore().doc(`orders/${stockNumber}`).set({
      customerPays: customerPaysUpdate,
      internalAmount: internalVal,
      revenue: newRevenue,
    }, { merge: true });

    setOrder(prev => ({
      ...prev,
      customerPays: customerPaysUpdate,
      internalAmount: internalVal,
      revenue: newRevenue,
    }));

    // Internal → "trade" purchase doc. Skipped when car is in 'service' status.
    if (car.status === 'service') return;

    const expense = {
      date: moment().format('MM/DD/YYYY'),
      isPayable: true,
      stock: car.stock,
      account: 'trade',
      amount: internalVal,
      memo: stockNumber,
      vendor: 'Skyway Classics',
    };
    firebase.firestore().doc(`purchases/${stockNumber}`).set(expense, { merge: true });
  };

  const handleCustomerBlur = () => saveAllocation(customerInput, internalInput);
  const handleInternalBlur = () => saveAllocation(customerInput, internalInput);

  // Convert a legacy SO (no customerPays) to the split-allocation model.
  // - Skyway customer: defaults to customer pays $0 (everything internal). Mirrors
  //   the old "entire revenue → trade expense" rule using the now-computed totalCost.
  // - Other customers: defaults to customer pays the existing revenue value (preserves
  //   what was billed). Internal becomes max(0, totalCost - revenue).
  // After conversion, the new split UI renders and the foreman can adjust the customer
  // amount from there. Writes the trade purchase doc (skipped when car is in 'service').
  const handleConvertToSplit = () => {
    const isSkywayCustomer = customer && customer.id === '9c0d88f5-84f9-454d-833d-a8ced9adad49';
    const defaultValue = isSkywayCustomer ? 0 : (Number(revenue) || 0);
    const projectedInternal = Math.max(0, Math.round((totalCost - defaultValue) * 100) / 100);

    const summary = isSkywayCustomer
      ? `Convert this SO to the customer/internal split model?\n\n` +
        `Customer Pays will default to $0 (Skyway customer = fully internal).\n` +
        `Internal (Skyway) will be $${projectedInternal.toFixed(2)} (= Labor $${laborCost.toFixed(2)} + Purchases $${purchasesTotal.toFixed(2)}).\n\n` +
        `You can adjust the Customer Pays amount after converting.`
      : `Convert this SO to the customer/internal split model?\n\n` +
        `Customer Pays will default to $${defaultValue.toFixed(2)} (current revenue).\n` +
        `Internal (Skyway) will be $${projectedInternal.toFixed(2)}.\n\n` +
        `You can adjust afterward.`;

    if (!window.confirm(summary)) return;

    const newCustomerPays = { type: 'amount', value: defaultValue };
    const newInternalCost = projectedInternal;
    const newRevenue = Math.round((defaultValue + newInternalCost) * 100) / 100;

    lastSyncedCustomerRef.current = defaultValue;
    lastSyncedInternalRef.current = newInternalCost;
    lastSyncedRevenueRef.current = newRevenue;

    setRevenue(newRevenue);
    setRevenueInput(newRevenue ? newRevenue.toString() : '');
    setCustomerInput(defaultValue ? String(defaultValue) : '');
    setInternalInput(newInternalCost ? String(newInternalCost) : '');

    firebase.firestore().doc(`orders/${stockNumber}`).set({
      customerPays: newCustomerPays,
      internalAmount: newInternalCost,
      revenue: newRevenue,
    }, { merge: true });

    setOrder(prev => ({
      ...prev,
      customerPays: newCustomerPays,
      internalAmount: newInternalCost,
      revenue: newRevenue,
    }));

    if (car.status !== 'service') {
      const expense = {
        date: moment().format('MM/DD/YYYY'),
        isPayable: true,
        stock: car.stock,
        account: 'trade',
        amount: newInternalCost,
        memo: stockNumber,
        vendor: 'Skyway Classics',
      };
      firebase.firestore().doc(`purchases/${stockNumber}`).set(expense, { merge: true });
    }

    StateManager.setAlertAndOpen('Converted to split allocation', 'success');
  };

  const handleAddPurchase = () => {
    setSelectedExpense(null);
    setExpenseModalOpen(true);
  };

  const handleAddDeposit = () => {
    setSelectedDeposit(null);
    setDepositModalOpen(true);
  };

  const handleDeleteExpense = async (expenseId) => {
    if (window.confirm("Are you sure you want to delete this expense?")) {
      StateManager.setLoading(true);
      try {
        await firebase.firestore().collection("purchases").doc(expenseId).delete();
        StateManager.setLoading(false);
      } catch (error) {
        console.error('Error deleting expense:', error);
        StateManager.setLoading(false);
        StateManager.setAlertAndOpen("Error deleting expense", "error");
      }
    }
  };

  const handleDeleteDeposit = async (depositId) => {
    if (window.confirm("Are you sure you want to delete this deposit?")) {
      StateManager.setLoading(true);
      try {
        await firebase.firestore().collection("deposits").doc(depositId).delete();
        StateManager.setLoading(false);
      } catch (error) {
        console.error('Error deleting deposit:', error);
        StateManager.setLoading(false);
        StateManager.setAlertAndOpen("Error deleting deposit", "error");
      }
    }
  };

  const formatCurrency = (amount) => {
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Recompute the Estimate tab's total here so it can be shown / synced without
  // leaving the Accounting tab. The synced amount is the full customer-facing
  // total (sales tax included).
  const estimateTotals = React.useMemo(() => computeEstimateTotals(order), [
    order.services, order.parts, order.subcontracts,
    order.selectedServiceIds, order.selectedPartIds, order.selectedSubcontractIds,
    order.laborRate, order.shopSuppliesRate, order.wasteFee, order.discount,
    order.taxEnabled, order.documentType, order.flatServiceTotal, order.flatPartsTotal,
    order.customer,
  ]);

  const canEditMoney = !order.disabled && StateManager.isBackoffice();

  const syncRevenueFromEstimate = () => {
    if (!estimateTotals) return;
    commitRevenue(estimateTotals.total);
    StateManager.setAlertAndOpen('Revenue synced from estimate', 'success');
  };
  const syncCustomerFromEstimate = () => {
    if (!estimateTotals) return;
    saveAllocation(String(estimateTotals.total), internalInput);
    StateManager.setAlertAndOpen('Customer amount synced from estimate', 'success');
  };
  const syncInternalFromEstimate = () => {
    if (!estimateTotals) return;
    saveAllocation(customerInput, String(estimateTotals.total));
    StateManager.setAlertAndOpen('Internal amount synced from estimate', 'success');
  };

  // Subtle icon button placed on a revenue/allocation line to pull the estimate total in.
  const SyncFromEstimateButton = ({ onClick }) => {
    if (!estimateTotals) return null;
    return (
      <Tooltip title={`Sync from estimate (${formatCurrency(estimateTotals.total)})`}>
        <span>
          <IconButton
            size="small"
            onClick={onClick}
            disabled={!canEditMoney}
            sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
          >
            <SyncIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  };


  return (
    <Box sx={{ py: 2 }}>
      {/* P&L Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            P&L:
          </Typography>
          {/* Reversing a contractor's pay for this order is an admin action, so it
              only appears for admins. */}
          {StateManager.isAdmin() && (
            <Tooltip title="Reverse a contractor's pay for this order">
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<UndoIcon />}
                onClick={() => setClawbackOpen(true)}
              >
                Clawback
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Subtle estimate readout so the foreman can see the current estimate
            total without switching to the Estimate tab. */}
        {estimateTotals && (
          <Tooltip
            title={
              <Box sx={{ fontSize: '0.75rem' }}>
                <div>Service: {formatCurrency(estimateTotals.serviceTotal)}</div>
                <div>Parts: {formatCurrency(estimateTotals.partsTotal)}</div>
                {estimateTotals.subcontractTotal > 0 && <div>Subcontract: {formatCurrency(estimateTotals.subcontractTotal)}</div>}
                <div>Shop supplies: {formatCurrency(estimateTotals.shopSuppliesFee)}</div>
                <div>Waste fee: {formatCurrency(estimateTotals.wasteFee)}</div>
                {estimateTotals.discount > 0 && <div>Discount: −{formatCurrency(estimateTotals.discount)}</div>}
                <div>Tax: {formatCurrency(estimateTotals.tax)}</div>
              </Box>
            }
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RequestQuoteIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                Estimate total:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {formatCurrency(estimateTotals.total)}
                </Box>
                {estimateTotals.documentType === 'flat-invoice'
                  ? ' (flat invoice)'
                  : estimateTotals.documentType === 'invoice'
                    ? ' (invoice)'
                    : ''}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {!useSplitModel && (StateManager.isAdmin() || StateManager.isManager()) && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<SwapHorizIcon />}
                onClick={handleConvertToSplit}
                disabled={order.disabled}
              >
                Convert
              </Button>
            }
          >
            This SO uses the legacy revenue model. Convert to the customer/internal split to use the new allocation UI.
          </Alert>
        )}

        {useSplitModel ? (
          <>
            {/* Customer billed amount */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 500 }}>Customer:</Typography>
                <SyncFromEstimateButton onClick={syncCustomerFromEstimate} />
              </Box>
              <TextField
                type="text"
                value={customerInput}
                onChange={(e) => setCustomerInput(e.target.value)}
                onBlur={handleCustomerBlur}
                disabled={order.disabled || !StateManager.isBackoffice()}
                placeholder="0.00"
                sx={{ width: 200 }}
                inputProps={{ style: { textAlign: 'right' } }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Box>
            {/* Internal (Skyway covers) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 500 }}>
                  Internal (Skyway):
                  {car.status === 'service' && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      not posted — car in service
                    </Typography>
                  )}
                </Typography>
                <SyncFromEstimateButton onClick={syncInternalFromEstimate} />
              </Box>
              <TextField
                type="text"
                value={internalInput}
                onChange={(e) => setInternalInput(e.target.value)}
                onBlur={handleInternalBlur}
                disabled={order.disabled || !StateManager.isBackoffice()}
                placeholder="0.00"
                sx={{ width: 200 }}
                inputProps={{ style: { textAlign: 'right' } }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Box>
            {/* Revenue (computed: customer + internal) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Revenue:</Typography>
              <Typography sx={{ textAlign: 'right', fontWeight: 700, width: 200 }}>
                {formatCurrency(splitRevenue)}
              </Typography>
            </Box>
          </>
        ) : (
          /* Revenue — legacy single-amount input */
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontWeight: 500 }}>1. Revenue:</Typography>
              <SyncFromEstimateButton onClick={syncRevenueFromEstimate} />
            </Box>
            <TextField
              type="text"
              value={revenueInput}
              onChange={handleRevenueChange}
              onBlur={handleRevenueBlur}
              disabled={order.disabled || !StateManager.isBackoffice()}
              placeholder="0.00"
              sx={{ width: 200 }}
              inputProps={{ style: { textAlign: 'right' } }}
            />
          </Box>
        )}

        {/* Labor breakdown — shown when both service-based and clocked labor contribute. */}
        {showLaborBreakdown && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontWeight: 500, pl: 2, color: 'text.secondary' }}>Service Labor:</Typography>
              <Typography sx={{ textAlign: 'right', color: 'text.secondary', width: 200 }}>
                {formatCurrency(serviceLabor)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontWeight: 500, pl: 2, color: 'text.secondary' }}>
                Clocked Labor:
                {timeLogs.some(l => l.status === 'active') && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: 'success.main' }}>
                    • live
                  </Typography>
                )}
              </Typography>
              <Typography sx={{ textAlign: 'right', color: 'text.secondary', width: 200 }}>
                {formatCurrency(clockedLabor)}
              </Typography>
            </Box>
          </>
        )}

        {/* Labor Cost (total) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>{useSplitModel ? 'Labor Cost:' : '2. Labor Cost:'}</Typography>
          <Typography sx={{ textAlign: 'right', color: 'text.secondary', width: 200 }}>
            {formatCurrency(laborCost)}
          </Typography>
        </Box>

        {/* Purchases */}
        <Box>
          <Box 
            onClick={() => setPurchasesExpanded(!purchasesExpanded)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>{useSplitModel ? 'Purchases:' : '3. Purchases:'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ textAlign: 'right', fontWeight: 500, width: 200 }}>
                {formatCurrency(purchasesTotal)}
              </Typography>
              {purchasesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          </Box>
          
          {/* Purchases Dropdown */}
          <Collapse in={purchasesExpanded}>
            <Box sx={{ px: 2, py: 2, bgcolor: 'background.default' }}>
              {order.expenses && order.expenses.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableBody>
                        {[...(order.expenses)].sort((a, b) => {
                          const parseDate = (d) => d ? moment(d, ['YYYY/MM/DD', 'MM/DD/YYYY', 'YYYY-MM-DD'], true).valueOf() || 0 : 0;
                          return parseDate(b.date) - parseDate(a.date);
                        }).map((expense) => (
                          <TableRow key={expense.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>{expense.memo || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[expense.date, expense.vendor].filter(Boolean).join(' · ') || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" fontWeight={700}>{formatCurrency(expense.amount || 0)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  if (expense.link) {
                                    window.open(expense.link, '_blank');
                                  } else {
                                    setSelectedExpense(expense);
                                    setExpenseModalOpen(true);
                                  }
                                }}
                              >
                                {expense.link ? <LaunchIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteExpense(expense.id)}
                                disabled={order.disabled}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddPurchase}
                    disabled={order.disabled}
                  >
                    Add Purchase
                  </Button>
                </>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No purchases recorded
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddPurchase}
                    disabled={order.disabled}
                  >
                    Add Purchase
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {useSplitModel ? (
          <>
            {/* Total Cost */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Total Cost:</Typography>
              <Typography sx={{ textAlign: 'right', fontWeight: 700, width: 200 }}>
                {formatCurrency(totalCost)}
              </Typography>
            </Box>
            {/* Profit / Loss (Revenue − Total Cost) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                {splitProfit >= 0 ? 'Profit:' : 'Loss:'}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'right',
                  fontWeight: 700,
                  color: splitProfit >= 0 ? 'success.main' : 'error.main',
                  width: 200,
                }}
              >
                {formatCurrency(splitProfit)}
              </Typography>
            </Box>
          </>
        ) : (
          /* Profit (legacy) */
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>4. Profit:</Typography>
            <Typography
              sx={{
                textAlign: 'right',
                fontWeight: 700,
                color: profit >= 0 ? 'success.main' : 'error.main',
                width: 200,
              }}
            >
              {formatCurrency(profit)}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Accounts Receivable Section */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Accounts Receivable:
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RequestQuoteIcon />}
            onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
          >
            Print Receipt
          </Button>
          <Menu
            anchorEl={printMenuAnchor}
            open={!!printMenuAnchor}
            onClose={() => setPrintMenuAnchor(null)}
          >
            <MenuItem onClick={openStatement}>Payment Statement</MenuItem>
            <MenuItem onClick={openInvoice}>Full Invoice</MenuItem>
          </Menu>
        </Box>

        {/* Deposits */}
        <Box>
          <Box 
            onClick={() => setDepositsExpanded(!depositsExpanded)}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              py: 1.5,
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>Deposits:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ textAlign: 'right', fontWeight: 500, width: 200 }}>
                {formatCurrency(depositsAmount)}
              </Typography>
              {depositsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          </Box>
          
          {/* Deposits Dropdown */}
          <Collapse in={depositsExpanded}>
            <Box sx={{ px: 2, py: 2, bgcolor: 'background.default' }}>
              {order.deposits && order.deposits.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Memo</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {order.deposits.map((deposit) => (
                          <TableRow key={deposit.id}>
                            <TableCell>{deposit.date || '-'}</TableCell>
                            <TableCell>{deposit.memo || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(deposit.amount || 0)}</TableCell>
                            <TableCell align="center">
                              {deposit.id && (
                                <IconButton
                                  size="small"
                                  title="Print payment receipt"
                                  onClick={() => openDepositReceipt(deposit.id)}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => { setSelectedDeposit(deposit); setDepositModalOpen(true); }}
                                disabled={order.disabled}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDeposit(deposit.id)}
                                disabled={order.disabled}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddDeposit}
                    disabled={order.disabled}
                  >
                    Add Deposit
                  </Button>
                </>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No deposits recorded
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddDeposit}
                    disabled={order.disabled}
                  >
                    Add Deposit
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Sales Tax */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            mt: 1,
          }}
        >
          <Typography sx={{ fontWeight: 500 }}>Sales Tax:</Typography>
          <TextField
            type="text"
            value={salesTaxInput}
            onChange={handleSalesTaxChange}
            onBlur={handleSalesTaxBlur}
            disabled={order.disabled || !StateManager.isBackoffice()}
            placeholder="0.00"
            sx={{ width: 200 }}
            inputProps={{ style: { textAlign: 'right' } }}
          />
        </Box>

        {/* Total */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Total:</Typography>
          <Typography 
            sx={{ 
              textAlign: 'right', 
              fontWeight: 700,
              width: 200
            }}
          >
            {formatCurrency(total)}
          </Typography>
        </Box>

        {/* Amount Owed */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Amount Owed:</Typography>
          <Typography 
            sx={{ 
              textAlign: 'right', 
              fontWeight: 700,
              color: (total - depositsAmount) > 0 ? 'error.main' : 'success.main',
              width: 200
            }}
          >
            {formatCurrency(total - depositsAmount)}
          </Typography>
        </Box>

        {/* Collect payment — charge a card on file */}
        <Box sx={{ mt: 2 }}>
          <SavedCardsList
            customer={order.customer || {}}
            mode="charge"
            defaultAmount={Math.max(0, (total || revenue || 0) - depositsAmount)}
            chargeContext={{
              type: 'service',
              reference: stockNumber,
              description: `${stockNumber} | ${paymentDescription} | service`,
            }}
            title="Collect Payment"
          />
          {/* Affidavit authorizing the charge above — signed before it is run. */}
          <Box sx={{ mt: 1.5 }}>
            <CreditCardAffidavitButton
              parentType="serviceOrder"
              refId={stockNumber}
              customer={order.customer || {}}
              defaultAmount={Math.max(0, (total || revenue || 0) - depositsAmount)}
              vehicleLabel={paymentDescription}
            />
          </Box>
        </Box>

        {/* Request payment — send the customer a link to pay themselves */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <RequestPaymentButton
            customer={order.customer || {}}
            reference={stockNumber}
            type="service"
            title={paymentDescription}
            defaultAmount={Math.max(0, (total || revenue || 0) - depositsAmount)}
            label="Request Payment"
          />
        </Box>
      </Box>

      <ExpenseModal
        open={expenseModalOpen}
        onClose={() => { setExpenseModalOpen(false); setSelectedExpense(null); }}
        expense={selectedExpense}
        stockNumber={stockNumber}
        onSaved={(updated) => {
          if (selectedExpense) {
            setOrder(prev => ({
              ...prev,
              expenses: (prev.expenses || []).map(e => e.id === updated.id ? { ...e, ...updated } : e),
            }));
          } else {
            setOrder(prev => ({ ...prev, expenses: [...(prev.expenses || []), updated] }));
          }
        }}
      />
      <DepositModal
        open={depositModalOpen}
        onClose={() => { setDepositModalOpen(false); setSelectedDeposit(null); }}
        stockNumber={stockNumber}
        deposit={selectedDeposit}
        type="service"
        onSaved={() => {
          setDepositModalOpen(false);
          setSelectedDeposit(null);
        }}
      />
      {StateManager.isAdmin() && (
        <ClawbackDialog
          open={clawbackOpen}
          orderId={stockNumber}
          carLabel={paymentDescription.trim()}
          revenue={revenue}
          onClose={() => setClawbackOpen(false)}
        />
      )}
    </Box>
  );
}

